// Google Analytics 4 (GA4) utility functions
// Track custom events, user actions, and conversions

/**
 * Send a custom event to Google Analytics
 * @param {string} eventName - Name of the event
 * @param {object} parameters - Event parameters
 */
export const trackEvent = (eventName, parameters = {}) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, {
      ...parameters,
      // Add timestamp for better tracking
      custom_timestamp: new Date().toISOString(),
    });
  }
};

/**
 * Track page views (for SPA navigation)
 * @param {string} pageTitle - Page title
 * @param {string} pagePath - Page path
 */
export const trackPageView = (pageTitle, pagePath) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', 'G-2HGJC6WBMN', {
      page_title: pageTitle,
      page_location: window.location.href,
      page_path: pagePath,
    });
  }
};

/**
 * Track user signup/registration
 * @param {string} method - Signup method (email, google, etc.)
 */
export const trackSignup = (method = 'email') => {
  trackEvent('sign_up', {
    method: method,
    event_category: 'user_engagement',
  });
};

/**
 * Track user login
 * @param {string} method - Login method
 */
export const trackLogin = (method = 'email') => {
  trackEvent('login', {
    method: method,
    event_category: 'user_engagement',
  });
};

/**
 * Track task creation (key conversion)
 * @param {number} taskId - Task ID
 * @param {string} category - Task category
 * @param {number} budget - Task budget
 */
export const trackTaskCreated = (taskId, category, budget) => {
  trackEvent('task_created', {
    task_id: taskId,
    task_category: category,
    task_budget: budget,
    event_category: 'conversions',
    value: budget, // For conversion value tracking
  });
};

/**
 * Track task offer submission
 * @param {number} taskId - Task ID
 * @param {number} offerAmount - Offer amount
 */
export const trackOfferSubmitted = (taskId, offerAmount) => {
  trackEvent('offer_submitted', {
    task_id: taskId,
    offer_amount: offerAmount,
    event_category: 'conversions',
    value: offerAmount,
  });
};

/**
 * Track message sent in chat
 * @param {number} conversationId - Conversation ID
 */
export const trackMessageSent = (conversationId) => {
  trackEvent('message_sent', {
    conversation_id: conversationId,
    event_category: 'user_engagement',
  });
};

/**
 * Track search performed
 * @param {string} searchTerm - Search query
 * @param {number} resultsCount - Number of results
 */
export const trackSearch = (searchTerm, resultsCount) => {
  trackEvent('search', {
    search_term: searchTerm,
    results_count: resultsCount,
    event_category: 'user_engagement',
  });
};

/**
 * Track task completion
 * @param {number} taskId - Task ID
 * @param {number} completionValue - Task completion value
 */
export const trackTaskCompleted = (taskId, completionValue) => {
  trackEvent('task_completed', {
    task_id: taskId,
    completion_value: completionValue,
    event_category: 'conversions',
    value: completionValue,
  });
};

/**
 * Set user properties for better segmentation
 * @param {object} userProps - User properties
 */
export const setUserProperties = (userProps) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', 'G-2HGJC6WBMN', {
      user_properties: userProps,
    });
  }
};

/**
 * Track errors for debugging
 * @param {string} errorMessage - Error message
 * @param {string} errorLocation - Where the error occurred
 */
export const trackError = (errorMessage, errorLocation) => {
  trackEvent('exception', {
    description: errorMessage,
    fatal: false,
    error_location: errorLocation,
    event_category: 'errors',
  });
};